import { StatusBar, Style } from "@capacitor/status-bar";
import {
  Bars3Icon,
  BellIcon,
  BriefcaseIcon,
  BuildingStorefrontIcon,
  ChevronLeftIcon,
  EnvelopeIcon,
  NewspaperIcon,
  ShoppingCartIcon,
  TagIcon,
  UserGroupIcon,
  UsersIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import HeaderButton from "@src/components/nav/HeaderButton";
import NavUserElement from "@src/components/nav/NavUserElement";
import { useAppSelector } from "@src/state/hooks";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import tw, { TwStyle } from "twin.macro";

type Props = {
  dark?: boolean;
  sideBarOnClick?: () => void;
};

const NavBar = ({ dark, sideBarOnClick }: Props) => {
  type tabsType = {
    title: string;
    href: string;
    style?: TwStyle;
    icon: JSX.Element;
    disabled?: boolean;
    activeLinks?: string[];
  };
  StatusBar.setStyle({ style: Style.Light });
  const { t } = useTranslation();
  const { user } = useAppSelector(state => state.user);

  const tabsMobile: Array<tabsType> = [
    {
      title: t("main.header.links.messenger.title"),
      href: "/messenger",
      icon: <EnvelopeIcon className="heroicon-stroke-w-1.2" />,
      style: tw`max-xl:hidden`,
    },
    {
      title: t("main.header.links.contacts"),
      href: "/kontakte/für_dich",
      icon: <UsersIcon className="heroicon-stroke-w-1.2" />,
    },
    {
      title: t("main.header.links.marketplace"),
      href: "/marketplace/für_dich",
      icon: <BuildingStorefrontIcon className="heroicon-stroke-w-1.2" />,
      activeLinks: ["/marketplace", "/verwaltung/mein_shop"],
    },
    {
      title: t("main.header.links.marketplace"),
      href: "/benachrichtigungen",
      icon: <BellIcon className="heroicon-stroke-w-1.2" />,
    },
    {
      title: t("main.header.links.advertisements"),
      href: "/inserate",
      icon: <TagIcon className="size-7 rotate-180 -scale-y-100" />,
      activeLinks: ["/inserate", "/verwaltung/inserate"],
    },
    {
      title: t("main.header.links.jobs"),
      href: "/jobs",
      icon: <BriefcaseIcon className="heroicon-stroke-w-1.2" />,
      activeLinks: ["/jobs", "/verwaltung/jobs"],
    },
  ];

  const tabs: Array<tabsType> = [
    {
      title: t("main.header.links.messenger.title"),
      href: "/messenger",
      icon: <EnvelopeIcon className="heroicon-stroke-w-1.2" />,
    },
    {
      title: t("main.header.links.contacts"),
      href: "/kontakte/für_dich",
      icon: <UsersIcon className="heroicon-stroke-w-1.2" />,
    },
    {
      title: t("main.header.links.groups.title"),
      href: "/gruppen",
      icon: <UserGroupIcon className="heroicon-stroke-w-1.2" />,
    },
    {
      title: t("main.header.links.feed"),
      href: "/news",
      icon: <NewspaperIcon className="heroicon-stroke-w-1.2" />,
    },
    {
      title: t("main.header.links.watch"),
      href: "/watch",
      icon: <VideoCameraIcon className="heroicon-stroke-w-1.2" />,
    },
    {
      title: t("main.header.links.marketplace"),
      href: "/marketplace/für_dich",
      icon: <BuildingStorefrontIcon className="heroicon-stroke-w-1.2" />,
      activeLinks: ["/marketplace", "/verwaltung/mein_shop"],
    },
    {
      title: t("main.header.links.advertisements"),
      href: "/inserate",
      icon: <TagIcon className="size-7 rotate-180 -scale-y-100" />,
      activeLinks: ["/inserate", "/verwaltung/inserate"],
    },
    {
      title: t("main.header.links.jobs"),
      href: "/jobs",
      icon: <BriefcaseIcon className="heroicon-stroke-w-1.2" />,
      activeLinks: ["/jobs", "/verwaltung/jobs"],
    },
  ];

  return (
    <div css={[tw`z-30 top-0 border-b border-gray-300 bg-white w-full`, dark && tw`bg-slate-900`]}>
      <div className="-mb-0.5 flex flex-row items-center gap-2 align-middle lg:pt-0.5 xl:px-5">
        {sideBarOnClick && (
          <button onClick={() => sideBarOnClick()}>
            <Bars3Icon className="heroicon-stroke-w-1.2 size-7 text-white" />
          </button>
        )}
        <NavLink to="/news" className="relative h-12 w-56 shrink-0 cursor-pointer max-xl:hidden">
          <img src="/logo.gif" alt="TRADEFOOX" css={[dark && tw`invert grayscale contrast-200`]} />
        </NavLink>

        {!user && (
          <NavLink
            css={[
              tw`bg-darkblue px-3 py-1 rounded-xl text-white max-xl:hidden text-sm`,
              dark && tw`border-white border bg-transparent`,
            ]}
            to="/registrierung"
          >
            {t("main.header.links.register")}
          </NavLink>
        )}
        <div className="h-12 xl:hidden"></div>

        <div className="flex w-full items-center justify-center gap-2 max-xl:hidden lg:gap-4 xl:gap-8">
          {tabs
            .filter(item => !item?.disabled)
            .map(tabData => {
              return (
                <HeaderButton
                  key={tabData.href}
                  title={tabData.title}
                  icon={tabData.icon}
                  href={tabData.href}
                  style={tabData.style}
                  dark={dark}
                  activeLinks={tabData.activeLinks}
                />
              );
            })}
          <div className="max-xl:hidden">
            <NavUserElement dark={dark} />
          </div>
        </div>
        <div className="relative mr-[100px] shrink-0 cursor-pointer max-xl:hidden">
          <NavLink to={"/marketplace/warenkorb"}>
            <div className="flex flex-row">
              <div className="relative size-[35px]  border-darkblue bg-transparent">
                <ShoppingCartIcon className="heroicon-stroke-w-1.2 size-8 text-darkblue" />
                <div className="absolute -right-px top-0">
                  <div className="flex size-[16px] items-center justify-center rounded-full border  bg-red-300 text-xs">
                    2
                  </div>
                </div>
              </div>
              <div className="ml-1 mt-2 text-xs text-darkblue">
                {t("main.shoppingVenture.shoppingCart")}
              </div>
              <ChevronLeftIcon className="mt-1.5 size-5 text-gray-300" />
            </div>
          </NavLink>
        </div>
        <div className="mr-0 flex w-full items-center justify-around gap-2 lg:gap-4 xl:hidden xl:w-fit xl:gap-8 2xl:mr-[250px]">
          {tabsMobile
            .filter(item => !item?.disabled)
            .map(tabData => {
              return (
                <HeaderButton
                  key={tabData.href}
                  title={tabData.title}
                  icon={tabData.icon}
                  href={tabData.href}
                  style={tabData.style}
                  dark={dark}
                  activeLinks={tabData.activeLinks}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default NavBar;
